import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import {
  Upload, FileText, X, AlertCircle, CheckCircle2,
  Loader2, Zap, ArrowRight, Layers,
} from "lucide-react";
import CandidatePreview from "../components/CandidatePreview";
import BatchUploadPanel from "../components/BatchUploadPanel";
import CandidateSearchBar from "../components/CandidateSearchBar";
import { useParseHistory } from "../hooks/useParseHistory";

const ACCEPTED_TYPES = {
  "application/pdf": [".pdf"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
  "application/msword": [".doc"],
  "application/rtf": [".rtf"],
  "text/rtf": [".rtf"],
};

function FilePreview({ file, onRemove }) {
  const ext = file.name.split(".").pop().toUpperCase();
  const size = (file.size / 1024).toFixed(0);
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "10px 14px", background: "var(--ink-800)",
      border: "1px solid var(--slate-border)", borderRadius: 8,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 6,
        background: "rgba(200,241,53,0.1)", border: "1px solid rgba(200,241,53,0.2)",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <FileText size={16} color="var(--acid)" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, color: "#E8EDF3", fontWeight: 500,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{file.name}</div>
        <div style={{ fontSize: 11, color: "var(--slate-muted)", fontFamily: "JetBrains Mono, monospace" }}>
          {ext} · {size} KB
        </div>
      </div>
      <button onClick={onRemove} style={{
        width: 24, height: 24, borderRadius: 6,
        background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.2)",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", flexShrink: 0, color: "#F87171",
      }}>
        <X size={12} />
      </button>
    </div>
  );
}

export default function UploadPage() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [originalFile, setOriginalFile] = useState(null);
  const [stage, setStage] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [currentFile, setCurrentFile] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [reuploadCandidate, setReuploadCandidate] = useState(null);

  const { addEntry } = useParseHistory();

  const onDrop = useCallback((accepted) => {
    setFiles((prev) => {
      const merged = [...prev, ...accepted];
      // deduplicate by name+size, keep max 20
      const seen = new Set();
      return merged
        .filter((f) => {
          const key = `${f.name}:${f.size}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .slice(0, 20);
    });
    setPreview(null);
    setResult(null);
    setError(null);
    setStage("idle");
    setSessionExpired(false);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxFiles: 20,
    maxSize: 10 * 1024 * 1024,
  });

  const handleParse = async () => {
    if (!files[0]) return;
    setStage("parsing");
    setProgress(0);
    setError(null);
    setCurrentFile(files[0]);
    setOriginalFile(files[0]);

    try {
      const fd = new FormData();
      fd.append("file", files[0]);

      const ticker = setInterval(() => {
        setProgress((p) => (p < 85 ? p + 5 : p));
      }, 300);

      const res = await fetch("/api/parse-preview", { method: "POST", body: fd, headers: { "X-Session-ID": localStorage.getItem("session_id") || "" } });
      clearInterval(ticker);
      setProgress(100);

      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail?.detail || `HTTP ${res.status}`);
      }

      const data = await res.json();
      setPreview(data);
      setStage("preview");
    } catch (err) {
      setStage("error");
      setError(err.message || "Parse failed");
    }
  };

  const handleConfirm = async (editedCandidate) => {
    setStage("submitting");
    try {
      const fd = new FormData();
      fd.append("candidate_json", JSON.stringify(editedCandidate));
      fd.append("filename", preview.filename);
      if (originalFile) fd.append("file", originalFile);
      if (reuploadCandidate?.id) fd.append("candidate_id", reuploadCandidate.id);

      const res = await fetch("/api/push-to-zoho", { method: "POST", body: fd, headers: { "X-Session-ID": localStorage.getItem("session_id") || "" } });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        if (res.status === 401 || detail?.error === "token_expired" || /INVALID_TOKEN|invalid oauth token/i.test(detail?.detail || "")) {
          setSessionExpired(true);
          setStage("error");
          setError("token_expired");
          return;
        }
        throw new Error(detail?.detail || `HTTP ${res.status}`);
      }

      const data = await res.json();
      setResult(data);
      setStage("success");
      addEntry(editedCandidate, preview.filename);
    } catch (err) {
      setStage("error");
      const msg = err.message || "Push to Zoho failed";
      if (/INVALID_TOKEN|invalid oauth token/i.test(msg)) {
        setSessionExpired(true);
      }
      setError(msg);
    }
  };

  const handleCancelPreview = () => {
    setPreview(null);
    setStage("idle");
  };

  const reset = () => {
    setFiles([]);
    setOriginalFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    setStage("idle");
    setProgress(0);
    setSessionExpired(false);
    setReuploadCandidate(null);
  };

  const isParsing = stage === "parsing";
  const isPreview = stage === "preview";
  const isSubmitting = stage === "submitting";
  const isSuccess = stage === "success";
  const isError = stage === "error";
  const isIdle = stage === "idle";
  const isBatchMode = files.length >= 2;

  return (
    <div style={{
      maxWidth: isBatchMode ? 900 : isPreview ? "100%" : 900,
      margin: "0 auto",
      padding: isPreview && !isBatchMode ? "40px 24px" : "40px 32px",
    }}>

      {/* Page header */}
      <div style={{ marginBottom: 36 }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "4px 10px", background: "rgba(200,241,53,0.08)",
          border: "1px solid rgba(200,241,53,0.2)", borderRadius: 999, marginBottom: 14,
        }}>
          {isBatchMode ? <Layers size={11} color="var(--acid)" /> : <Zap size={11} color="var(--acid)" />}
          <span style={{
            fontFamily: "JetBrains Mono, monospace", fontSize: 10,
            color: "var(--acid)", letterSpacing: "0.06em",
          }}>{isBatchMode ? "Batch Mode" : "GPT-4o Powered"}</span>
        </div>
        <h1 style={{
          fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: 32,
          color: "#F0F4FA", margin: 0, marginBottom: 8, letterSpacing: "-0.02em",
        }}>Parse CV</h1>
        <p style={{ color: "var(--slate-text)", margin: 0, fontSize: 15 }}>
          Upload one or more resumes to extract structured candidate data
        </p>
      </div>

      {/* Candidate search — link this upload to an existing candidate for reupload */}
      {!isBatchMode && (
        <CandidateSearchBar
          selectedCandidate={reuploadCandidate}
          onSelect={setReuploadCandidate}
          onClearSelected={() => setReuploadCandidate(null)}
        />
      )}

      {/* Batch mode panel */}
      {isBatchMode && (
        <BatchUploadPanel files={files} onReset={reset} />
      )}

      {/* Single-file stepper */}
      {!isBatchMode && !isIdle && !isError && (
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          marginBottom: 28, fontSize: 12, color: "var(--slate-muted)",
          fontFamily: "JetBrains Mono, monospace",
        }}>
          {[
            { key: "parsing", label: "01 Parse" },
            { key: "preview", label: "02 Review" },
            { key: "success", label: "03 Push" },
          ].map(({ key, label }, i, arr) => {
            const stageOrder = { parsing: 0, preview: 1, submitting: 2, success: 2 };
            const currentOrder = stageOrder[stage] ?? 0;
            const stepOrder = stageOrder[key] ?? i;
            const active = stepOrder === currentOrder;
            const done = stepOrder < currentOrder;
            return (
              <React.Fragment key={key}>
                <span style={{
                  color: done ? "#4ADE80" : active ? "var(--acid)" : "var(--slate-muted)",
                  fontWeight: active ? 700 : 400,
                }}>
                  {done ? "✓ " : ""}{label}
                </span>
                {i < arr.length - 1 && (
                  <span style={{ color: "var(--slate-border)" }}>→</span>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Single-file upload panel */}
      {!isBatchMode && <div>

        {/* Dropzone */}
        {(isIdle || isError) && (
          <div
            {...getRootProps()}
            className={isDragActive ? "drop-active" : ""}
            style={{
              border: "2px dashed",
              borderColor: isDragActive ? "var(--acid)" : "var(--slate-border)",
              borderRadius: 12, padding: "48px 32px", textAlign: "center",
              cursor: "pointer",
              background: isDragActive ? "rgba(200,241,53,0.04)" : "var(--ink-800)",
              transition: "all 0.2s ease", marginBottom: 16,
            }}
          >
            <input {...getInputProps()} />
            <div style={{
              width: 56, height: 56, borderRadius: 14,
              background: isDragActive ? "rgba(200,241,53,0.15)" : "var(--ink-700)",
              border: `1px solid ${isDragActive ? "rgba(200,241,53,0.3)" : "var(--slate-border)"}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 20px", transition: "all 0.2s ease",
            }}>
              <Upload size={22} color={isDragActive ? "var(--acid)" : "var(--slate-text)"} strokeWidth={1.5} />
            </div>
            <div style={{
              fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 16,
              color: isDragActive ? "var(--acid)" : "#E8EDF3", marginBottom: 8,
            }}>
              {isDragActive ? "Release to upload" : "Drop CV here"}
            </div>
            <div style={{ fontSize: 13, color: "var(--slate-muted)" }}>
              or click to browse · PDF, DOCX, RTF · до 20 файлов · max 10MB
            </div>
          </div>
        )}

        {/* File chip */}
        {files[0] && (isIdle || isError) && (
          <FilePreview
            file={files[0]}
            onRemove={() => { setFiles([]); setStage("idle"); }}
          />
        )}

        {/* Progress bar */}
        {isParsing && (
          <div style={{ marginTop: 16 }}>
            <div style={{
              display: "flex", alignItems: "center",
              justifyContent: "space-between", marginBottom: 8,
            }}>
              <span style={{
                fontSize: 12, color: "var(--slate-text)",
                display: "flex", alignItems: "center", gap: 6,
              }}>
                <Loader2 size={12} color="var(--acid)"
                  style={{ animation: "spin 1s linear infinite" }} />
                {progress < 100 ? "Uploading…" : "Analysing with GPT-4o…"}
              </span>
              <span style={{
                fontFamily: "JetBrains Mono, monospace",
                fontSize: 11, color: "var(--acid)",
              }}>{progress}%</span>
            </div>
            <div style={{
              height: 3, background: "var(--ink-700)",
              borderRadius: 999, overflow: "hidden",
            }}>
              <div style={{
                height: "100%", width: `${progress}%`,
                background: "var(--acid)", borderRadius: 999,
                transition: "width 0.3s ease",
              }} />
            </div>
          </div>
        )}

        {/* Error */}
        {isError && (
          <div style={{
            marginTop: 14, padding: "12px 14px",
            background: "rgba(248,113,113,0.08)",
            border: "1px solid rgba(248,113,113,0.2)",
            borderRadius: 8, display: "flex", gap: 10, alignItems: "flex-start",
          }}>
            <AlertCircle size={15} color="#F87171" style={{ flexShrink: 0, marginTop: 1 }} />
            {sessionExpired ? (
              <div>
                <div style={{ fontSize: 13, color: "#F87171", fontWeight: 500, marginBottom: 6 }}>
                  Сессия истекла. Пожалуйста, войдите заново.
                </div>
                <button
                  onClick={() => navigate("/login")}
                  style={{
                    padding: "6px 14px",
                    background: "rgba(248,113,113,0.15)",
                    border: "1px solid rgba(248,113,113,0.4)",
                    borderRadius: 6, fontSize: 12, color: "#F87171",
                    fontWeight: 600, cursor: "pointer",
                  }}
                >
                  Войти снова
                </button>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 13, color: "#F87171", fontWeight: 500, marginBottom: 2 }}>
                  {preview ? "Push to Zoho failed" : "Parse failed"}
                </div>
                <div style={{ fontSize: 12, color: "var(--slate-text)" }}>{error}</div>
              </div>
            )}
          </div>
        )}

        {/* Success banner */}
        {isSuccess && (
          <div style={{
            padding: "14px 16px", background: "rgba(74,222,128,0.06)",
            border: "1px solid rgba(74,222,128,0.2)", borderRadius: 8,
            marginBottom: 14,
          }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 10, marginBottom: 8,
            }}>
              <CheckCircle2 size={15} color="#4ADE80" />
              <span style={{ fontSize: 13, color: "#4ADE80", fontWeight: 500 }}>
                {currentFile?.name} pushed to Zoho Recruit
              </span>
              <button onClick={reset} style={{
                marginLeft: "auto", background: "none",
                border: "1px solid rgba(139,151,168,0.2)", borderRadius: 6,
                padding: "3px 10px", fontSize: 11, color: "var(--slate-text)",
                cursor: "pointer", fontFamily: "DM Sans, sans-serif",
              }}>
                Parse another
              </button>
            </div>
            {result?.zoho_url && (
              <a
                href={result.zoho_url}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  fontSize: 13, color: "var(--acid)",
                  textDecoration: "none",
                  borderBottom: "1px solid rgba(200,241,53,0.4)",
                  paddingBottom: 1,
                }}
              >
                Открыть карточку кандидата в Zoho Recruit -&gt;
              </a>
            )}
          </div>
        )}

        {/* Parse button */}
        {files[0] && isIdle && (
          <button
            onClick={handleParse}
            style={{
              marginTop: 16, width: "100%", padding: "12px 20px",
              background: "var(--acid)", border: "none", borderRadius: 8,
              fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14,
              color: "var(--ink-950)", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: 8, letterSpacing: "0.02em", transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--acid-glow)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--acid)";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            <Zap size={15} strokeWidth={2.5} />
            Parse CV
            <ArrowRight size={14} strokeWidth={2.5} />
          </button>
        )}

        {/* Retry button */}
        {isError && !sessionExpired && files[0] && (
          <button onClick={handleParse} style={{
            marginTop: 12, width: "100%", padding: "10px 20px",
            background: "rgba(248,113,113,0.1)",
            border: "1px solid rgba(248,113,113,0.3)", borderRadius: 8,
            fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 13,
            color: "#F87171", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
            Try again
          </button>
        )}

      </div>}

      {/* Preview — split layout (single-file only) */}
      {!isBatchMode && isPreview && preview && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginTop: 24 }}>

          {/* Левая колонка — PDF */}
          <div style={{
            position: "sticky",
            top: 24,
            height: "calc(100vh - 80px)",
            borderRadius: 12,
            border: "1px solid var(--slate-border)",
            overflow: "hidden",
            background: "var(--ink-900)",
          }}>
            {originalFile?.type === "application/pdf" ? (
              <iframe
                src={URL.createObjectURL(originalFile)}
                style={{ width: "100%", height: "100%", border: "none" }}
                title="CV Preview"
              />
            ) : (
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                height: "100%", flexDirection: "column", gap: 12,
                color: "var(--slate-muted)", fontSize: 13,
              }}>
                <FileText size={32} color="var(--slate-border)" />
                <span>Предпросмотр недоступен для {originalFile?.name?.split(".").pop().toUpperCase()}</span>
                <span style={{ fontSize: 11 }}>Откройте файл отдельно для проверки</span>
              </div>
            )}
          </div>

          {/* Правая колонка — карточка */}
          <div style={{ overflowY: "auto" }}>
            <CandidatePreview
              data={preview.parsed}
              filename={preview.filename}
              onConfirm={handleConfirm}
              onCancel={handleCancelPreview}
              loading={isSubmitting}
            />
          </div>

        </div>
      )}

      {/* Submitting hint (single-file only) */}
      {!isBatchMode && isSubmitting && (
        <div style={{
          marginTop: 16, display: "flex", alignItems: "center", gap: 8,
          fontSize: 13, color: "var(--slate-text)",
        }}>
          <Loader2 size={14} color="var(--acid)"
            style={{ animation: "spin 1s linear infinite" }} />
          Pushing to Zoho Recruit…
        </div>
      )}

    </div>
  );
}
